import { useState, useEffect } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";

// Router
import { NavLink, withRouter } from "react-router-dom";

// Redux
import { connect } from "react-redux";
import useLoanRepaymentReport from "../../custom-hooks/useLoanRepaymentReport";
import { showModal } from "./../../redux/actions/modal";

// UI Components
import ButtonLoader, { Loader } from "../../UI/Loaders/Loaders";
import Paginator from "../Pagination/Pagination.component";
import { formatMoney } from "../../helpers/formatter";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import useAllAggregators from "../../custom-hooks/useAllAggregators";
import { BASE_URL } from "../../appConstants";
import { getTokenFromLocalStorage } from "../../helpers/localStorage";
import { appInsights } from "../../config/appInsights";

const today = new Date().toISOString().slice(0, 10);
const lastMonth = new Date(new Date());
lastMonth.setDate(lastMonth.getDate() - 30);
const aMonthAgo = lastMonth.toISOString().slice(0, 10);

const LoanRepaymentReport = (props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(null);
  const [size, setSize] = useState(10);

  const [startDate, setStartDate] = useState(aMonthAgo);
  const [endDate, setEndDate] = useState(today);
  const [date, setDate] = useState({
    FromDate: aMonthAgo,
    ToDate: today,
  });
  const [aggregatorState, setAggregatorState] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    appInsights.trackPageView({
      name: "Loan Repayment Report - (LoanRepaymentReport.js)",
      isLoggedIn: true,
    });
  }, []);

  //  Methods
  const modalHandler = (modalData, type) => {
    props.showModal({
      modalIdentifier: "audittrail",
      dataRecord: modalData,
      action: "customrange",
      type,
    });
  };
  const handleDownload = () => {
    setDownloadLoading(true);

    let token = getTokenFromLocalStorage();
    let queryParams = "";
    if (startDate && endDate) {
      queryParams = `?FromDate=${startDate}&ToDate=${endDate}`;
    }
    if (aggregatorState) {
      queryParams = queryParams
        ? `${queryParams}&AggregatorId=${aggregatorState}`
        : `?AggregatorId=${aggregatorState}`;
    }

    fetch(BASE_URL + `/eco/repayment_schedule_report/csv${queryParams}`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        Authorization: "Token " + token,
      },
      // body: JSON.stringify(data)
    })
      .then((response) => {
        setDownloadLoading(false);

        if (response.status === 200 || response.status === 201) {
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${
                startDate && endDate
                  ? `LoanInvestmentReport from ${startDate} to ${endDate}.csv`
                  : `Loan_Investment_Report.csv`
              }`
            );
            link.click();
          });
        }

        if (response.status === 403 || response.status === 401) {
          localStorage.removeItem("sso_auth_token");
          this.props.history.push("/login");
        }

        if (response.status >= 500) {
          // this.setState({ error: "Something went wrong. Please check your internet connection and try again" });
        }
      })
      .catch((error) => {
        appInsights.trackException({
          exception: error,
          properties: {
            fileName: "LoanRepaymentReport.js",
          },
        });
        // this.setState({ loading: false, error: "Something went wrong. Please check your internet connection and try again" });
        setDownloadLoading(false);
      });
  };
  const resetTable = () => {
    setCount(null);
    setPageNumber(1);
  };

  const filterData = (e) => {
    e.preventDefault();

    const diffTime = new Date(endDate) - new Date(startDate);
    if (diffTime < 0) {
      modalHandler("", "alert");
      return;
    }

    resetTable();
    setDate({
      FromDate: startDate,
      ToDate: endDate,
    });
  };

  //  Life Cycle Hooks
  useEffect(() => {
    //  If user is not authenticates, redirect to login
    if (!props.isAuthenticated) {
      props.history.push("/login");
    }
  }, [props.history, props.isAuthenticated, props.loggedInUser]);

  //JSX Template
  let RenderedComponent = null;

  const { data, status, error } = useLoanRepaymentReport({
    FromDate: date.FromDate,
    ToDate: date.ToDate,
    Page: pageNumber,
    PageSize: size,
    AggregatorId: aggregatorState,
  });
  const { data: aggregatorData } = useAllAggregators();

  if (data) {
    if (count === null) {
      setCount(data.totalCount);
    }
  }

  if (!data && status !== "loading") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Repayment Reports
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading") {
    RenderedComponent = (
      <Loader centered="true" text="Loading Repayment Reports..." />
    );
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              repayment reports, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (data && data.data && data.data.length === 0) ||
      (data && !data.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no logged
              activities.
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <div className="row m-x-sm m-y-2 grid__padding">
          <MDBTable responsive minimal="true" bordered hover>
            <MDBTableHead>
              <tr>
                <th>S/N</th>
                <th>Aggregator Name</th>
                <th>Loan Id</th>
                <th>Loan Reference</th>
                <th>Product Name</th>
                <th>Borrower Name</th>
                {/* <th>Currency</th> */}
                <th>Principal</th>
                <th>Interest</th>
                <th>Penalty</th>
                <th>Principal Paid</th>
                <th>Interest Paid</th>
                <th>Penalty Paid</th>
                <th>Amount Due</th>
                <th>Installment</th>
                {/* <th>Date Made</th> */}
                <th>Due Date</th>
                <th>Days Past Due</th>
                {/* <th>Fees</th> */}
                {/* <th>Fees Paid</th> */}
                <th>Is Paid</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {data &&
                data.data.map((loanRepaymentReport, _id) => {
                  const {
                    aggregatorName,
                    loanId,
                    loanRef,
                    productName,
                    borrowerName,
                    currency,
                    principal,
                    interest,
                    penalty,
                    principalPaid,
                    interestPaid,
                    penaltyPaid,
                    amountToRepay,
                    installment,
                    // dateMade,
                    dateDue,
                    daysOverdue,
                    // fee,
                    // feesPaid,
                    isPaid,
                  } = loanRepaymentReport;
                  return (
                    <tr key={_id}>
                      <td>{(pageNumber - 1) * size + (_id + 1)}</td>
                      <td>{aggregatorName}</td>
                      <td>{loanId}</td>
                      <td>{loanRef}</td>
                      <td>{productName}</td>
                      <td>{borrowerName}</td>
                      {/* <td>{currency}</td> */}
                      <td>
                        {principal
                          ? formatMoney(principal, currency)
                          : principal}
                      </td>
                      <td>
                        {interest ? formatMoney(interest, currency) : interest}
                      </td>
                      <td>{penalty}</td>
                      <td>
                        {principalPaid
                          ? formatMoney(principalPaid, currency)
                          : principalPaid}
                      </td>

                      <td>
                        {interestPaid
                          ? formatMoney(interestPaid, currency)
                          : interestPaid}
                      </td>
                      <td>{penaltyPaid}</td>
                      <td>
                        {amountToRepay
                          ? formatMoney(amountToRepay, currency)
                          : amountToRepay}
                      </td>
                      {/* amount to Repay is changed on table to amount Due */}
                      <td>{installment}</td>
                      {/* <td>{dateMade}</td> */}
                      <td>{dateDue.substring(0, 10)}</td>
                      <td>{daysOverdue}</td>
                      {/* Days overdue is changed on table head to Days past due */}
                      {/* <td>{fee}</td> */}
                      {/* <td>{feesPaid}</td> */}
                      <td>{!isPaid ? "Not Paid" : " Paid"}</td>
                    </tr>
                  );
                })}
            </MDBTableBody>
          </MDBTable>
        </div>
      );
    }
  }
  return (
    <div className="datatable-wrapper">
      <div className="table-view speed-1x fadeInRight">
        {/*Repayment Reports Header*/}
        <div className="block-header">
          <div className="row">
            <div className="col-lg-8 col-md-6 col-sm-12 dashboard-title flex__row">
              {/* <h3 className="m-l-1">
                <aside className="rounder">1</aside>
                &nbsp; Reports
              </h3> */}

              <div className="breadcrumb__location  ">
                <NavLink to="dashboard">
                  <h3 className="breadcrumb__location__title">Dashboard</h3>
                </NavLink>
                <span className="breadcrumb__location__icon">
                  <i className="fas fa-angle-double-right" />
                </span>
                <NavLink to="loan-repayment-reports">
                  <h3 className="breadcrumb__location__title">
                    Loan Repayment Reports
                  </h3>
                </NavLink>
              </div>
            </div>
          </div>
          <div className="d-flex flex-row flex-wrap justify-content-between">
            <div className="d-flex flex-row flex-wrap justify-content-start m-l-1 m-t-1">
              <label htmlFor="table-fetch" className="form-group">
                Number of results:{" "}
                <select
                  onChange={(e) => {
                    resetTable();
                    setSize(e.target.value);
                  }}
                  id="table-fetch"
                  className="table-fetch-select m-l-1"
                  value={size}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <form className="d-flex flex-row m-t-md" onSubmit={filterData}>
              <div>
                <CustomSelectDropdown
                  onChange={(e) => setAggregatorState(e.target.value)}
                  name="AggregatorId"
                  value={aggregatorState}
                  className="round user__form form-control"
                >
                  <option value="">search by aggregator</option>
                  {aggregatorData &&
                    aggregatorData.map((aggregator) => {
                      const { biz_name, aggregator_id } = aggregator;
                      return (
                        <option
                          value={Number(aggregator_id)}
                          key={aggregator_id}
                        >
                          {biz_name}
                        </option>
                      );
                    })}
                </CustomSelectDropdown>
              </div>
              <div
                className="d-flex flex-row flex-wrap justify-content-start"
                style={{ alignItems: "flex-end" }}
              >
                <div className="m-l-1 m-r-1 m-b-1 form-group">
                  <label htmlFor="table-fetch">Start Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    defaultValue={aMonthAgo}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="m-l-1 m-r-1 m-b-1 form-group">
                  <label htmlFor="table-fetch">End Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    defaultValue={today}
                    max={today}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <button
                  className="m-l-1 m-r-1 zoom-element btn login-btn"
                  type="submit"
                  value="submit"
                >
                  Search
                </button>
                {/* Start of download button */}
                <button
                  type="button"
                  className="btn advancly-white-btn  "
                  onClick={handleDownload}
                  disabled={status === "loading" || downloadLoading}
                >
                  <i className="fas fa-download m-r-1" />
                  Download {downloadLoading && <ButtonLoader />}
                </button>

                {/* end of download button */}
              </div>
            </form>
          </div>
        </div>

        {/*Datatable || Loader || Error Message*/}
        {RenderedComponent}

        <Paginator
          size={size}
          page={pageNumber}
          count={count}
          changeCurrentPage={(pageNumber) => setPageNumber(pageNumber)}

          //testing
          //   size={search.PageSize}
          //     page={search.Page}
          //     count={count}
          //     changeCurrentPage={(Page) => setSearch({ ...search, Page })}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    authReducer: { isAuthenticated, loggedInUser },
  } = state;

  return {
    isAuthenticated,
    loggedInUser,
  };
};

export default withRouter(
  connect(mapStateToProps, { showModal })(LoanRepaymentReport)
);
